import { createSlice } from '@reduxjs/toolkit';
import { filteredDeals } from './pipeline';
import { StageObject } from '../../params/redux/pipeline';
import { ChartState, DealBreakDownObject } from '../../params/redux/chart';
import { fetchAgencysClientAnalyticsThunk, fetchCustomerAnalyticsDataThunk, fetchDealsBreakDownForSalesThunk, fetchSalesAnalyticsDataThunk } from '../thunks/chart';

// Initial state with default values for authentication and user info
const initialState: ChartState = {
    sales_dashboard: {
        total_deals: 0,
        total_revenue: '0.00',
        customer_acquired: 0,
        conversion_rate: 0,
        salesAnalytics: {},
        dealBreakDown: [],
        donutCategories: [],
        donutChartSeries: []
    },
    customer_dashboard: {
        customerAnalytics: {},
        total_leads: 0,
        total_conversions: 0,
        total_revenue: 0
    },
    client_agency_dashboard: {
        active_companies: 0,
        revenue_per_company: 0,
        company_lifecycle: 0,
        retention_rate: 0
    },
    lineChart: [],
    categories: [],
    loaders: {
        isLoading: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isSearching: false,
        isInserting: false
    }
};

// Slice for app-related states and actions
const chartSlice = createSlice({
    // Slice name
    name: 'chart',
    // inital state for the slice
    initialState,
    reducers: {
        settingSalesAnalyticsData: (state, action) => {
            state.sales_dashboard.salesAnalytics = action.payload;
        },
        settingDonutChartData: (state, action) => {
            const { selectedPipeline } = action.payload;
            if (selectedPipeline && selectedPipeline.pipeline_stages) {
                selectedPipeline.pipeline_stages = selectedPipeline.pipeline_stages.map((stage: StageObject) => ({
                    ...stage,
                    deals: filteredDeals(stage)
                }));
                state.sales_dashboard.donutCategories = selectedPipeline.pipeline_stages.map((stage: StageObject) => (
                    `${stage.stage_name.substring(0, 10)} ${stage.stage_name.length > 10 && '...'} ${stage.deals.length} Deals`
                ));
                state.sales_dashboard.donutChartSeries = selectedPipeline.pipeline_stages.map((stage: StageObject) => stage.deals.length);
            } else {
                state.sales_dashboard.donutChartSeries = [];
                state.sales_dashboard.donutCategories = [];
            }
        },
        // Resets loading states related to app actions
        resettingChartLoader: (state) => {
            state.loaders = initialState.loaders;
        },
        // Resets all app data to the initial state
        resettingChartData: () => initialState
    },
    extraReducers: (builder) => {
        // Handle async actions for login
        builder
            .addCase(fetchSalesAnalyticsDataThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
                state.sales_dashboard.salesAnalytics = {};
                state.sales_dashboard.total_deals = 0;
                state.sales_dashboard.conversion_rate = 0;
                state.sales_dashboard.total_revenue = '0.00';
                state.sales_dashboard.customer_acquired = 0;
            })
            .addCase(fetchSalesAnalyticsDataThunk.fulfilled, (state, action) => {
                const { total_deals, total_revenue, customer_acquired, conversion_rate, graph_data } = action.payload as {
                    total_deals: number;
                    total_revenue: string;
                    customer_acquired: number;
                    conversion_rate: number;
                    graph_data: object;
                };
                state.sales_dashboard.salesAnalytics = graph_data;
                state.sales_dashboard.total_deals = total_deals;
                state.sales_dashboard.total_revenue = total_revenue;
                state.sales_dashboard.customer_acquired = customer_acquired;
                state.sales_dashboard.conversion_rate = conversion_rate;
            })
            .addCase(fetchSalesAnalyticsDataThunk.rejected, (state) => {
                state.sales_dashboard.salesAnalytics = {};
                state.sales_dashboard.total_deals = 0;
                state.sales_dashboard.conversion_rate = 0;
                state.sales_dashboard.total_revenue = '0.00';
                state.sales_dashboard.customer_acquired = 0;
            });
        builder
            .addCase(fetchCustomerAnalyticsDataThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
                state.customer_dashboard.customerAnalytics = {};
                state.customer_dashboard.total_leads = 0;
                state.customer_dashboard.total_conversions = 0;
            })
            .addCase(fetchCustomerAnalyticsDataThunk.fulfilled, (state, action) => {
                const { total_leads, lead_conversion, graph_data } = action.payload as {
                    total_leads: number;
                    lead_conversion: number;
                    graph_data: object;
                };
                state.customer_dashboard.customerAnalytics = graph_data;
                state.customer_dashboard.total_leads = total_leads;
                state.customer_dashboard.total_conversions = lead_conversion;
            })
            .addCase(fetchCustomerAnalyticsDataThunk.rejected, (state) => {
                state.customer_dashboard.customerAnalytics = {};
                state.customer_dashboard.total_leads = 0;
                state.customer_dashboard.total_conversions = 0;
            });
        builder
            .addCase(fetchDealsBreakDownForSalesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
                state.sales_dashboard.dealBreakDown = [];
            })
            .addCase(fetchDealsBreakDownForSalesThunk.fulfilled, (state, action) => {
                state.sales_dashboard.dealBreakDown = action.payload as DealBreakDownObject[];
            })
            .addCase(fetchDealsBreakDownForSalesThunk.rejected, (state) => {
                state.sales_dashboard.dealBreakDown = [];
            });
        builder
            .addCase(fetchAgencysClientAnalyticsThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
                state.client_agency_dashboard.active_companies = 0;
                state.client_agency_dashboard.company_lifecycle = 0;
                state.client_agency_dashboard.retention_rate = 0;
                state.client_agency_dashboard.revenue_per_company = 0;
            })
            .addCase(fetchAgencysClientAnalyticsThunk.fulfilled, (state, action) => {
                const { total_active_companies, revenue_per_company, company_lifecycle, retention_rate } = action.payload as {
                    total_active_companies: number;
                    revenue_per_company: number;
                    company_lifecycle: number;
                    retention_rate: number;
                };
                state.client_agency_dashboard.active_companies = total_active_companies;
                state.client_agency_dashboard.revenue_per_company = revenue_per_company;
                state.client_agency_dashboard.company_lifecycle = company_lifecycle;
                state.client_agency_dashboard.retention_rate = retention_rate;
            })
            .addCase(fetchAgencysClientAnalyticsThunk.rejected, (state) => {
                state.client_agency_dashboard.active_companies = 0;
                state.client_agency_dashboard.company_lifecycle = 0;
                state.client_agency_dashboard.retention_rate = 0;
                state.client_agency_dashboard.revenue_per_company = 0;
            });
    }
});

// Exporting actions for updating app state
export const {
    settingSalesAnalyticsData,
    settingDonutChartData,
    resettingChartData,
    resettingChartLoader
} = chartSlice.actions;

// Exporting the reducer to be used in the store
export default chartSlice.reducer;
